import { useEffect, useState, Fragment } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  MessageCircle, Save, PlugZap, Loader2, CheckCircle2, XCircle,
  AlertCircle, Webhook, Copy as CopyIcon, Info, Send, RefreshCcw, ListChecks,
  Inbox, User as UserIcon, Users, ArrowLeft, Activity, Trash2, ChevronDown, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";

const CURRENT_ORIGIN =
  typeof window !== "undefined"
    ? window.location.origin.replace(".preview.emergentagent.com", "").replace(/^https?:\/\//, "")
    : "";

export default function WhatsAppTab() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  // Form fields — access_token/verify_token/app_secret only get written when
  // the user actively types a value. Blank keeps the stored secret intact.
  const [form, setForm] = useState({
    access_token: "",
    phone_number_id: "",
    waba_id: "",
    verify_token: "",
    app_secret: "",
    dry_run: true,
    test_recipient: "",
    enabled_events: [],
    event_templates: {},
  });
  const [templates, setTemplates] = useState([]);
  const [tplLoading, setTplLoading] = useState(false);
  const [tplResult, setTplResult] = useState(null);   // {ok, total, approved_count, waba_id_used, waba_from_phone_id, waba_match, meta_status, error}
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  // Test form state
  const [test, setTest] = useState({
    to: "",
    template_name: "",
    language_code: "tr",
    variables: [],
  });
  const [sendingTest, setSendingTest] = useState(false);
  const [testResp, setTestResp] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/whatsapp/settings");
      setCfg(data);
      setForm((f) => ({
        ...f,
        phone_number_id: data.phone_number_id || "",
        waba_id: data.waba_id || "",
        dry_run: data.dry_run !== false,
        test_recipient: data.test_recipient || "",
        enabled_events: data.enabled_events || [],
        event_templates: data.event_templates || {},
      }));
      setTest((t) => ({ ...t, to: data.test_recipient || t.to }));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      // Only send fields the user typed; empty strings mean "keep saved value".
      await api.post("/whatsapp/settings", {
        access_token: form.access_token,
        phone_number_id: form.phone_number_id,
        waba_id: form.waba_id,
        verify_token: form.verify_token,
        app_secret: form.app_secret,
        dry_run: form.dry_run,
        test_recipient: form.test_recipient,
        enabled_events: form.enabled_events,
        event_templates: form.event_templates,
      });
      toast.success("Ayarlar kaydedildi");
      // Wipe sensitive inputs so they're not shown again
      setForm((f) => ({ ...f, access_token: "", verify_token: "", app_secret: "" }));
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const runTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post("/whatsapp/test");
      setTestResult(data);
      if (data.ok) toast.success("Bağlantı başarılı");
      else toast.error(data.detail || "Bağlantı başarısız");
    } catch (e) {
      setTestResult({ ok: false, detail: formatApiError(e) });
      toast.error(formatApiError(e));
    } finally { setTesting(false); }
  };

  const toggleEvent = (key) => {
    setForm((f) => {
      const cur = new Set(f.enabled_events || []);
      cur.has(key) ? cur.delete(key) : cur.add(key);
      return { ...f, enabled_events: Array.from(cur) };
    });
  };

  const setEventTemplate = (key, patch) => {
    setForm((f) => ({
      ...f,
      event_templates: {
        ...f.event_templates,
        [key]: { ...(f.event_templates[key] || { template_name: "", language_code: "tr" }), ...patch },
      },
    }));
  };

  const webhookUrl = typeof window !== "undefined"
    ? `${window.location.origin.replace(/\/$/, "").replace(":3000", "")}/api/whatsapp/webhook`
    : "";

  const copyWebhook = async () => {
    try {
      await navigator.clipboard.writeText(webhookUrl);
      toast.success("Webhook URL kopyalandı");
    } catch { /* ignore */ }
  };

  // ------- Templates -------
  const loadTemplates = async () => {
    setTplLoading(true);
    setTplResult(null);
    try {
      const { data } = await api.get("/whatsapp/templates");
      setTemplates(data.templates || []);
      setTplResult({
        ok: !data.error,
        total: data.total ?? 0,
        approved_count: data.approved_count ?? 0,
        waba_id_used: data.waba_id_used || "",
        waba_from_phone_id: data.waba_from_phone_id || "",
        waba_match: data.waba_match,   // true | false | null (belirsiz)
        meta_status: data.meta_status,
        error: data.error || "",
        fetched_at: new Date().toLocaleTimeString("tr-TR"),
      });
      if (data.error) {
        toast.error(`Meta: ${data.error}`);
      } else {
        toast.success(`${data.approved_count} onaylı template bulundu (toplam ${data.total})`);
      }
    } catch (e) {
      // Ağ/HTTP hatası
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || formatApiError(e);
      setTplResult({
        ok: false,
        total: 0,
        approved_count: 0,
        waba_id_used: "",
        error: `İstek başarısız (HTTP ${status || "?"}): ${detail}`,
        fetched_at: new Date().toLocaleTimeString("tr-TR"),
      });
      toast.error(detail);
    } finally { setTplLoading(false); }
  };

  useEffect(() => {
    if (cfg?.configured) {
      loadTemplates();
      loadLogs();
    }
    // eslint-disable-next-line
  }, [cfg?.configured]);

  const selectedTpl = templates.find((t) => t.name === test.template_name);

  // Parse {{1}}, {{2}}, ... placeholders from BODY component
  const varCount = (() => {
    if (!selectedTpl) return 0;
    const body = (selectedTpl.components || []).find((c) => (c.type || "").toUpperCase() === "BODY");
    const text = body?.text || "";
    const matches = text.match(/{{\s*\d+\s*}}/g) || [];
    return matches.length;
  })();

  useEffect(() => {
    // Sync variables array length to template placeholder count
    setTest((t) => {
      const next = [...(t.variables || [])];
      while (next.length < varCount) next.push("");
      while (next.length > varCount) next.pop();
      return { ...t, variables: next };
    });
  }, [varCount, test.template_name]);

  // Live preview
  const preview = (() => {
    if (!selectedTpl) return "";
    const body = (selectedTpl.components || []).find((c) => (c.type || "").toUpperCase() === "BODY");
    let text = body?.text || "";
    (test.variables || []).forEach((v, i) => {
      text = text.replaceAll(`{{${i + 1}}}`, v || `{{${i + 1}}}`);
    });
    return text;
  })();

  const sendTest = async () => {
    if (!test.to || !test.template_name) {
      toast.error("Alıcı ve template zorunlu");
      return;
    }
    setSendingTest(true);
    setTestResp(null);
    try {
      const { data } = await api.post("/whatsapp/send-test", {
        to: test.to,
        template_name: test.template_name,
        language_code: test.language_code || (selectedTpl?.language) || "tr",
        variables: test.variables || [],
      });
      setTestResp(data);
      if (data.duplicate) toast.info(data.detail);
      else if (data.sent) toast.success("Mesaj gönderildi ✓");
      else if (data.dry_run) toast.info(data.detail);
      else toast.error(data.error_detail || data.detail || "Gönderilemedi");
      loadLogs();
    } catch (e) {
      setTestResp({ ok: false, error_detail: formatApiError(e) });
      toast.error(formatApiError(e));
    } finally { setSendingTest(false); }
  };

  // ------- Logs -------
  const loadLogs = async () => {
    setLogsLoading(true);
    try {
      const { data } = await api.get("/whatsapp/logs?limit=30");
      setLogs(data.items || []);
    } catch { /* ignore */ }
    finally { setLogsLoading(false); }
  };

  // ------- Conversations (Inbox) -------
  const [conversations, setConversations] = useState([]);
  const [convLoading, setConvLoading] = useState(false);
  const [selectedPhone, setSelectedPhone] = useState(null);
  const [thread, setThread] = useState([]);
  const [threadLoading, setThreadLoading] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [replying, setReplying] = useState(false);

  const loadConversations = async () => {
    setConvLoading(true);
    try {
      const { data } = await api.get("/whatsapp/conversations");
      setConversations(data.items || []);
    } catch (e) { console.warn(e); }
    finally { setConvLoading(false); }
  };

  const openThread = async (phone) => {
    setSelectedPhone(phone);
    setThreadLoading(true);
    try {
      const { data } = await api.get(`/whatsapp/conversations/${encodeURIComponent(phone)}/messages`);
      setThread(data.messages || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setThreadLoading(false); }
  };

  const sendReply = async () => {
    if (!replyText.trim() || !selectedPhone) return;
    setReplying(true);
    try {
      const { data } = await api.post(
        `/whatsapp/conversations/${encodeURIComponent(selectedPhone)}/reply`,
        { text: replyText }
      );
      if (data.dry_run) toast.info(data.detail);
      else if (data.sent) toast.success("Yanıt gönderildi");
      else toast.error(data.error_detail || "Gönderilemedi");
      setReplyText("");
      await openThread(selectedPhone);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setReplying(false); }
  };

  useEffect(() => {
    if (cfg?.configured) loadConversations();
    // eslint-disable-next-line
  }, [cfg?.configured]);

  if (loading || !cfg) {
    return (
      <div className="p-8 text-center text-slate-500">
        <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
        <div className="text-xs font-mono uppercase">Yükleniyor...</div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Bağlantı Bilgileri */}
      <Card className="p-5 rounded-sm border-slate-800 bg-slate-950/40 space-y-4">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500 font-mono mb-1">
              Meta WhatsApp Business Cloud API
            </div>
            <h3 className="text-lg font-semibold text-slate-100 flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-emerald-500" /> Bağlantı Bilgileri
            </h3>
            <p className="text-xs text-slate-400 mt-1">
              Meta Developer Console'dan aldığınız kimlik bilgilerini girin. Token güvenli olarak sunucuda saklanır, frontend'e asla gönderilmez.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-[10px] uppercase font-mono px-2 py-1 rounded-sm border ${
              cfg.configured
                ? "border-emerald-800 text-emerald-400 bg-emerald-950/30"
                : "border-slate-700 text-slate-400 bg-slate-950/40"
            }`}>
              {cfg.configured ? "YAPILANDIRILDI" : "YAPILANDIRILMADI"}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="space-y-1.5 md:col-span-2">
            <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">
              Access Token {cfg.configured && <span className="text-[10px] text-emerald-500 normal-case ml-1">(kayıtlı — boş bırakırsanız değişmez)</span>}
            </Label>
            <Input
              data-testid="wa-access-token"
              type="password"
              placeholder={cfg.configured ? "••••••••••••••••" : "EAAG... uzun System User tokenı"}
              value={form.access_token}
              onChange={(e) => setForm({ ...form, access_token: e.target.value })}
              className="bg-slate-950 border-slate-700 rounded-sm font-mono"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Phone Number ID</Label>
            <Input
              data-testid="wa-phone-number-id"
              value={form.phone_number_id}
              onChange={(e) => setForm({ ...form, phone_number_id: e.target.value })}
              placeholder="Örn: 123456789012345"
              className="bg-slate-950 border-slate-700 rounded-sm font-mono"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">WABA ID (Business Account ID)</Label>
            <Input
              data-testid="wa-waba-id"
              value={form.waba_id}
              onChange={(e) => setForm({ ...form, waba_id: e.target.value })}
              placeholder="Örn: 987654321098765"
              className="bg-slate-950 border-slate-700 rounded-sm font-mono"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">
              Verify Token {cfg.verify_token_set && <span className="text-[10px] text-emerald-500 normal-case ml-1">(kayıtlı)</span>}
            </Label>
            <Input
              data-testid="wa-verify-token"
              type="password"
              placeholder={cfg.verify_token_set ? "••••••••" : "Webhook doğrulaması için siz belirlersiniz"}
              value={form.verify_token}
              onChange={(e) => setForm({ ...form, verify_token: e.target.value })}
              className="bg-slate-950 border-slate-700 rounded-sm font-mono"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">
              App Secret {cfg.app_secret_set && <span className="text-[10px] text-emerald-500 normal-case ml-1">(kayıtlı)</span>}
            </Label>
            <Input
              data-testid="wa-app-secret"
              type="password"
              placeholder={cfg.app_secret_set ? "••••••••" : "Webhook imza doğrulaması için (opsiyonel)"}
              value={form.app_secret}
              onChange={(e) => setForm({ ...form, app_secret: e.target.value })}
              className="bg-slate-950 border-slate-700 rounded-sm font-mono"
            />
          </div>
          <div className="space-y-1.5 md:col-span-2">
            <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">
              Test Alıcı Telefon Numarası (E.164 örn: +905321234567)
            </Label>
            <Input
              data-testid="wa-test-recipient"
              value={form.test_recipient}
              onChange={(e) => setForm({ ...form, test_recipient: e.target.value })}
              placeholder="+905XXXXXXXXX"
              className="bg-slate-950 border-slate-700 rounded-sm font-mono"
            />
            <div className="text-[10px] text-slate-500 font-mono">
              DRY-RUN kapatıldığında canlı gönderim <b>yalnızca bu numaraya</b> yapılabilir. İlk başta kendi telefonunuzu girin.
            </div>
          </div>
        </div>

        {/* Webhook bilgisi */}
        <div className="border-t border-slate-800 pt-3">
          <div className="text-xs uppercase tracking-wider text-slate-400 font-mono mb-2 flex items-center gap-1.5">
            <Webhook className="w-3.5 h-3.5" /> Meta Console'da Webhook URL olarak girin:
          </div>
          <div className="flex items-center gap-2">
            <Input
              readOnly
              value={webhookUrl}
              className="bg-slate-950 border-slate-700 rounded-sm font-mono text-xs"
              data-testid="wa-webhook-url"
            />
            <Button size="sm" variant="outline" onClick={copyWebhook} className="rounded-sm">
              <CopyIcon className="w-4 h-4" />
            </Button>
          </div>
          <div className="text-[10px] text-slate-500 mt-1 font-mono">
            Meta App → WhatsApp → Configuration → Callback URL + Verify Token
          </div>
        </div>

        <div className="flex items-center justify-between border-t border-slate-800 pt-3">
          <div className="flex items-center gap-3">
            <Switch
              data-testid="wa-dry-run"
              checked={form.dry_run}
              onCheckedChange={(v) => setForm({ ...form, dry_run: v })}
            />
            <div>
              <div className="text-sm text-slate-100">DRY-RUN Modu</div>
              <div className="text-[10px] text-slate-500 font-mono">
                {form.dry_run ? "AÇIK — gerçek mesaj gönderilmez, sadece log tutulur" : "KAPALI — gerçek mesaj gönderilir"}
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              data-testid="wa-test-btn"
              onClick={runTest}
              disabled={testing || !cfg.configured}
              variant="outline"
              className="border-slate-700 bg-transparent text-slate-300 hover:bg-slate-800 rounded-sm uppercase text-xs font-semibold"
            >
              {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <PlugZap className="w-4 h-4 mr-2" />}
              Bağlantıyı Test Et
            </Button>
            <Button
              data-testid="wa-save-btn"
              onClick={save}
              disabled={saving}
              className="bg-emerald-600 hover:bg-emerald-500 rounded-sm uppercase text-xs font-semibold"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Kaydet
            </Button>
          </div>
        </div>

        {testResult && (
          <div className={`flex items-start gap-2 rounded-sm border p-3 text-xs font-mono ${
            testResult.ok
              ? "border-emerald-800 bg-emerald-950/30 text-emerald-300"
              : "border-rose-900 bg-rose-950/30 text-rose-300"
          }`}>
            {testResult.ok ? <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" /> : <XCircle className="w-4 h-4 shrink-0 mt-0.5" />}
            <div className="flex-1">
              <div className="font-semibold">{testResult.ok ? "Başarılı" : "Başarısız"}</div>
              {testResult.detail && <div className="text-[11px] mt-0.5">{testResult.detail}</div>}
              {testResult.phone && (
                <pre className="text-[10px] mt-1 whitespace-pre-wrap">{JSON.stringify(testResult.phone, null, 2)}</pre>
              )}
            </div>
          </div>
        )}
      </Card>

      {/* Bildirim Olayları */}
      <Card className="p-5 rounded-sm border-slate-800 bg-slate-950/40 space-y-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-mono mb-1">
            Bildirim Olayları
          </div>
          <h3 className="text-lg font-semibold text-slate-100">Otomatik Bildirimler</h3>
          <p className="text-xs text-slate-400 mt-1">
            Her olay için WhatsApp template adı belirtin. Sadece Meta Manager'da <b>APPROVED</b> template'ler gönderilebilir. Dil kodu genellikle <code className="text-slate-300">tr</code>.
          </p>
        </div>
        <div className="space-y-2">
          {(cfg.supported_events || []).map((ev) => {
            const enabled = form.enabled_events?.includes(ev.key);
            const tpl = form.event_templates?.[ev.key] || { template_name: "", language_code: "tr" };
            return (
              <div
                key={ev.key}
                data-testid={`wa-event-row-${ev.key}`}
                className={`grid grid-cols-1 md:grid-cols-[auto_1fr_auto_auto] gap-2 items-center rounded-sm border p-2 ${
                  enabled ? "border-emerald-900 bg-emerald-950/10" : "border-slate-800 bg-slate-950/30"
                }`}
              >
                <Switch
                  data-testid={`wa-event-toggle-${ev.key}`}
                  checked={enabled}
                  onCheckedChange={() => toggleEvent(ev.key)}
                />
                <div className="text-sm text-slate-200">{ev.label}</div>
                <Input
                  data-testid={`wa-event-template-${ev.key}`}
                  placeholder="template_name (örn: siparis_olusturuldu_tr)"
                  value={tpl.template_name || ""}
                  onChange={(e) => setEventTemplate(ev.key, { template_name: e.target.value })}
                  disabled={!enabled}
                  className="bg-slate-950 border-slate-700 rounded-sm font-mono text-xs w-full md:w-64"
                />
                <Input
                  data-testid={`wa-event-lang-${ev.key}`}
                  placeholder="tr"
                  value={tpl.language_code || "tr"}
                  onChange={(e) => setEventTemplate(ev.key, { language_code: e.target.value })}
                  disabled={!enabled}
                  className="bg-slate-950 border-slate-700 rounded-sm font-mono text-xs w-16"
                />
              </div>
            );
          })}
        </div>
        <div className="flex items-start gap-2 border-t border-slate-800 pt-3 text-[11px] text-amber-400">
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
          <div>
            <b>Güvenlik notu:</b> DRY-RUN modu AÇIKKEN Meta'ya gerçek mesaj gitmez. Gerçek gönderim için önce test edip DRY-RUN'ı kapatın. Alıcı müşterinin <code>whatsapp_opt_in</code> alanı da true olmalı — aksi halde bildirim gitmez.
          </div>
        </div>
      </Card>

      {/* Test Mesajı */}
      <Card className="p-5 rounded-sm border-slate-800 bg-slate-950/40 space-y-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500 font-mono mb-1">
              Kontrollü Test
            </div>
            <h3 className="text-lg font-semibold text-slate-100 flex items-center gap-2">
              <Send className="w-5 h-5 text-emerald-500" /> Test Mesajı Gönder
            </h3>
            <p className="text-xs text-slate-400 mt-1">
              Meta'da <b>APPROVED</b> template'lerden birini seçin, değişkenleri doldurun ve gönderin.
              DRY-RUN modunda gerçek mesaj gitmez. Canlı modda yalnızca yukarıdaki "Test Alıcı" numarasına gönderim yapılabilir.
            </p>
          </div>
          <Button
            data-testid="wa-load-templates-btn"
            onClick={loadTemplates}
            disabled={tplLoading || !cfg.configured}
            variant="outline"
            className="border-slate-700 bg-transparent text-slate-300 hover:bg-slate-800 rounded-sm uppercase text-xs font-semibold"
          >
            {tplLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCcw className="w-4 h-4 mr-2" />}
            Template'leri Yenile
          </Button>
        </div>

        {/* Templates çağrı sonucu — waba_id_used, total, approved_count,
            meta_status ve error alanları görünür şekilde gösterilir. Sessiz
            "0 ONAYLI" durumu artık olmaz. */}
        {tplResult && (
          <div
            data-testid="wa-templates-result"
            className={`rounded-sm border p-3 text-xs space-y-1 ${
              tplResult.ok
                ? "border-emerald-800/60 bg-emerald-950/20 text-emerald-100"
                : "border-rose-800/60 bg-rose-950/20 text-rose-100"
            }`}
          >
            <div className="flex items-center justify-between gap-2 mb-1">
              <div className="font-mono uppercase tracking-widest text-[10px] flex items-center gap-1">
                {tplResult.ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
                Template Sorgu Sonucu · {tplResult.fetched_at}
              </div>
              {tplResult.meta_status && (
                <span className="text-[10px] font-mono opacity-80">meta_status: {tplResult.meta_status}</span>
              )}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-1">
              <div><span className="opacity-60">Toplam:</span> <b>{tplResult.total}</b></div>
              <div><span className="opacity-60">APPROVED:</span> <b>{tplResult.approved_count}</b></div>
              <div className="col-span-2 truncate">
                <span className="opacity-60">waba_id_used:</span>{" "}
                <b className="font-mono">{tplResult.waba_id_used || "—"}</b>
              </div>
            </div>
            {tplResult.waba_from_phone_id && (
              <div className={`mt-1 p-1.5 rounded-sm border text-[11px] ${
                tplResult.waba_match === true
                  ? "border-emerald-800 bg-emerald-950/40"
                  : "border-amber-800 bg-amber-950/40 text-amber-200"
              }`}>
                <div className="flex items-center gap-1">
                  {tplResult.waba_match ? "✅" : "⚠️"}
                  <span>
                    Phone Number ID'nin bağlı olduğu WABA (Meta'dan):{" "}
                    <b className="font-mono">{tplResult.waba_from_phone_id}</b>
                  </span>
                </div>
                {tplResult.waba_match === false && (
                  <div className="mt-0.5 text-[10px]">
                    ERP'ye kayıtlı WABA ID (<code>{tplResult.waba_id_used}</code>) ile Phone Number ID'nin bağlı olduğu WABA (<code>{tplResult.waba_from_phone_id}</code>) <b>UYUŞMUYOR</b>. WABA ID'yi Meta'daki gerçek değeriyle güncelleyin.
                  </div>
                )}
              </div>
            )}
            {tplResult.error && (
              <div className="mt-1 p-1.5 rounded-sm bg-rose-950/40 border border-rose-800 text-rose-100 whitespace-pre-wrap break-words font-mono text-[11px]">
                <b>Hata:</b> {tplResult.error}
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Alıcı Numarası</Label>
            <Input
              data-testid="wa-test-to"
              value={test.to}
              onChange={(e) => setTest({ ...test, to: e.target.value })}
              placeholder={form.test_recipient || "+905XXXXXXXXX"}
              className="bg-slate-950 border-slate-700 rounded-sm font-mono"
            />
            {form.test_recipient && test.to !== form.test_recipient && !form.dry_run && (
              <div className="text-[10px] text-rose-400 font-mono">
                Canlı gönderim için "Test Alıcı" ({form.test_recipient}) ile eşleşmeli
              </div>
            )}
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">
              Template ({templates.length} onaylı)
            </Label>
            <Select
              value={test.template_name || "__none"}
              onValueChange={(v) => {
                const tpl = templates.find((t) => t.name === v);
                setTest({
                  ...test,
                  template_name: v === "__none" ? "" : v,
                  language_code: tpl?.language || "tr",
                });
              }}
              disabled={!templates.length}
            >
              <SelectTrigger data-testid="wa-test-template-select" className="bg-slate-950 border-slate-700 rounded-sm font-mono">
                <SelectValue placeholder="Template seçin..." />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-800 rounded-sm max-h-80">
                <SelectItem value="__none">— Seçiniz —</SelectItem>
                {templates.map((t) => (
                  <SelectItem key={`${t.name}-${t.language}`} value={t.name}>
                    <span className="font-mono">{t.name}</span>
                    <span className="text-[10px] text-slate-500 ml-2">{t.language} · {t.category}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Dil Kodu</Label>
            <Input
              data-testid="wa-test-lang"
              value={test.language_code}
              onChange={(e) => setTest({ ...test, language_code: e.target.value })}
              className="bg-slate-950 border-slate-700 rounded-sm font-mono"
              placeholder="tr / en_US"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Mesaj Tipi</Label>
            <Input value="Template (BODY)" readOnly className="bg-slate-900 border-slate-800 rounded-sm text-slate-500 font-mono" />
          </div>
          {varCount > 0 && (
            <div className="md:col-span-2 space-y-2">
              <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">
                Değişkenler ({varCount})
              </Label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                {Array.from({ length: varCount }, (_, i) => (
                  <Input
                    key={i}
                    data-testid={`wa-test-var-${i + 1}`}
                    value={test.variables[i] || ""}
                    onChange={(e) => {
                      const v = [...test.variables];
                      v[i] = e.target.value;
                      setTest({ ...test, variables: v });
                    }}
                    placeholder={`{{${i + 1}}}`}
                    className="bg-slate-950 border-slate-700 rounded-sm font-mono"
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Preview */}
        {selectedTpl && (
          <div className="border border-slate-800 bg-slate-900/40 rounded-sm p-3">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mb-2">Önizleme</div>
            <div className="whitespace-pre-wrap text-sm text-slate-100 font-normal" data-testid="wa-test-preview">
              {preview}
            </div>
          </div>
        )}

        <div className="flex items-center justify-between border-t border-slate-800 pt-3">
          <div className="text-xs font-mono text-slate-500">
            DRY-RUN: <span className={form.dry_run ? "text-emerald-400" : "text-rose-400"}>{form.dry_run ? "AÇIK (güvenli)" : "KAPALI (canlı)"}</span>
          </div>
          <Button
            data-testid="wa-send-test-btn"
            onClick={sendTest}
            disabled={sendingTest || !test.template_name || !test.to || !cfg.configured}
            className="bg-emerald-600 hover:bg-emerald-500 rounded-sm uppercase text-xs font-semibold"
          >
            {sendingTest ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
            Test Mesajı Gönder
          </Button>
        </div>

        {testResp && (
          <div
            data-testid="wa-test-result"
            className={`rounded-sm border p-3 text-xs font-mono ${
              testResp.sent
                ? "border-emerald-800 bg-emerald-950/30 text-emerald-300"
                : testResp.dry_run
                ? "border-amber-800 bg-amber-950/20 text-amber-300"
                : "border-rose-900 bg-rose-950/30 text-rose-300"
            }`}
          >
            <div className="flex items-start gap-2">
              {testResp.sent ? <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
                : <XCircle className="w-4 h-4 shrink-0 mt-0.5" />}
              <div className="flex-1 space-y-1">
                <div className="font-semibold">
                  {testResp.sent ? "GÖNDERİLDİ" : testResp.dry_run ? "DRY-RUN (gönderilmedi)" : "BAŞARISIZ"}
                </div>
                {testResp.message_id && <div>Meta message_id: <b className="text-slate-100">{testResp.message_id}</b></div>}
                {testResp.created_at && <div>Zaman: {new Date(testResp.created_at).toLocaleString("tr-TR")}</div>}
                {testResp.error_code && <div>Hata kodu: <b>{testResp.error_code}</b></div>}
                {testResp.error_detail && <div>Hata: {testResp.error_detail}</div>}
                {testResp.duplicate && <div>Aynı mesaj 60sn içinde gönderildi (previous_message_id: {testResp.previous_message_id})</div>}
                {testResp.detail && !testResp.error_detail && <div>{testResp.detail}</div>}
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* Gönderim Logları */}
      <Card className="rounded-sm border-slate-800 bg-slate-950/40 overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-800">
          <div className="flex items-center gap-2">
            <ListChecks className="w-4 h-4 text-slate-400" />
            <span className="text-sm font-semibold text-slate-200">Son Gönderimler</span>
            <span className="text-xs font-mono text-slate-500">({logs.length})</span>
          </div>
          <Button onClick={loadLogs} disabled={logsLoading} size="sm" variant="ghost" className="rounded-sm">
            {logsLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCcw className="w-4 h-4" />}
          </Button>
        </div>
        {logs.length === 0 ? (
          <div className="p-6 text-center text-slate-500 text-sm">Henüz gönderim yok</div>
        ) : (
          <table className="w-full text-xs">
            <thead className="bg-slate-900/60 text-[10px] uppercase tracking-wider text-slate-500 font-mono">
              <tr>
                <th className="text-left px-3 py-2">Zaman</th>
                <th className="text-left px-3 py-2">Alıcı</th>
                <th className="text-left px-3 py-2">Template</th>
                <th className="text-left px-3 py-2">Durum</th>
                <th className="text-left px-3 py-2">message_id / Hata</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {logs.map((l, i) => (
                <tr key={i} data-testid={`wa-log-row-${i}`} className="hover:bg-slate-900/40">
                  <td className="px-3 py-2 font-mono text-slate-400">
                    {new Date(l.created_at).toLocaleString("tr-TR")}
                  </td>
                  <td className="px-3 py-2 font-mono text-slate-200">{l.to}</td>
                  <td className="px-3 py-2 font-mono text-slate-200">
                    {l.template_name} <span className="text-slate-500">·{l.language_code}</span>
                  </td>
                  <td className="px-3 py-2">
                    {l.dry_run ? (
                      <span className="text-amber-400 font-mono">DRY-RUN</span>
                    ) : l.ok ? (
                      <span className="text-emerald-400 font-mono">✓ OK</span>
                    ) : (
                      <span className="text-rose-400 font-mono">✗ HATA</span>
                    )}
                  </td>
                  <td className="px-3 py-2 font-mono text-slate-300 max-w-md truncate">
                    {l.message_id || l.error_detail || "-"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {/* Gelen Kutusu & Sohbetler */}
      <Card className="rounded-sm border-slate-800 bg-slate-950/40 overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-800">
          <div className="flex items-center gap-2">
            <Inbox className="w-4 h-4 text-emerald-400" />
            <span className="text-sm font-semibold text-slate-200">Gelen Kutusu & Sohbetler</span>
            <span className="text-xs font-mono text-slate-500">({conversations.length})</span>
          </div>
          <Button onClick={loadConversations} disabled={convLoading} size="sm" variant="ghost" className="rounded-sm">
            {convLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCcw className="w-4 h-4" />}
          </Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] min-h-[360px]">
          {/* Sol: kişi listesi */}
          <div className="border-r border-slate-800 max-h-[500px] overflow-y-auto">
            {conversations.length === 0 ? (
              <div className="p-6 text-center text-slate-500 text-sm">
                Henüz gelen mesaj yok. Meta Console'da webhook aktif ve müşteri size mesaj gönderince burada belirir.
              </div>
            ) : conversations.map((c) => (
              <button
                key={c.phone}
                data-testid={`wa-conv-${c.phone}`}
                onClick={() => openThread(c.phone)}
                className={`w-full text-left px-3 py-2.5 border-b border-slate-900 hover:bg-slate-900/60 transition-colors ${
                  selectedPhone === c.phone ? "bg-slate-900/80" : ""
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <UserIcon className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                  <span className="text-sm font-semibold text-slate-100 font-mono truncate">{c.phone}</span>
                  {c.unread > 0 && (
                    <span className="ml-auto text-[9px] uppercase font-mono bg-emerald-500 text-emerald-950 px-1.5 py-0.5 rounded-sm">
                      {c.unread}
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-slate-400 truncate">
                  <span className={c.last_direction === "in" ? "text-emerald-500" : "text-slate-500"}>
                    {c.last_direction === "in" ? "→" : "←"}
                  </span>{" "}
                  {c.last_text}
                </div>
                {c.last_at && (
                  <div className="text-[10px] text-slate-600 font-mono mt-0.5">
                    {new Date(c.last_at).toLocaleString("tr-TR")}
                  </div>
                )}
              </button>
            ))}
          </div>
          {/* Sağ: thread */}
          <div className="flex flex-col max-h-[500px]">
            {!selectedPhone ? (
              <div className="flex-1 flex items-center justify-center text-slate-500 text-sm p-6">
                Sol taraftan bir sohbet seçin
              </div>
            ) : threadLoading ? (
              <div className="flex-1 flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-slate-500" />
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2 px-4 py-2 border-b border-slate-800 bg-slate-900/40">
                  <button onClick={() => setSelectedPhone(null)} className="md:hidden text-slate-400">
                    <ArrowLeft className="w-4 h-4" />
                  </button>
                  <UserIcon className="w-4 h-4 text-slate-400" />
                  <span className="text-sm font-mono font-semibold text-slate-100">{selectedPhone}</span>
                </div>
                <div className="flex-1 overflow-y-auto p-4 space-y-2 bg-slate-950/30">
                  {thread.length === 0 && (
                    <div className="text-center text-xs text-slate-500 py-8">Mesaj yok</div>
                  )}
                  {thread.map((m, i) => (
                    <div
                      key={i}
                      data-testid={`wa-msg-${i}`}
                      className={`flex ${m.direction === "in" ? "justify-start" : "justify-end"}`}
                    >
                      <div
                        className={`max-w-[75%] rounded-sm px-3 py-1.5 text-sm ${
                          m.direction === "in"
                            ? "bg-slate-800 text-slate-100"
                            : m.dry_run
                            ? "bg-amber-950/40 border border-amber-800 text-amber-100"
                            : m.ok
                            ? "bg-emerald-700 text-emerald-50"
                            : "bg-rose-900 text-rose-100"
                        }`}
                      >
                        <div className="whitespace-pre-wrap break-words">{m.text}</div>
                        <div className="text-[9px] mt-1 opacity-70 font-mono">
                          {m.at && new Date(m.at).toLocaleString("tr-TR")}
                          {m.dry_run && " · DRY-RUN"}
                          {m.error_detail && ` · ${m.error_detail}`}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="border-t border-slate-800 p-2 flex gap-2 bg-slate-950/40">
                  <Input
                    data-testid="wa-reply-input"
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendReply(); } }}
                    placeholder="Yanıt yazın... (24s pencere içinde serbest metin)"
                    className="bg-slate-950 border-slate-700 rounded-sm text-sm flex-1"
                  />
                  <Button
                    data-testid="wa-reply-send-btn"
                    onClick={sendReply}
                    disabled={replying || !replyText.trim()}
                    className="bg-emerald-600 hover:bg-emerald-500 rounded-sm"
                  >
                    {replying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      </Card>

      {/* Webhook Tanı Paneli */}
      <WebhookDiagnosticPanel />

      {/* Otomatik Bildirimler */}
      <AutomationPanel />

      {/* Personel Bildirimleri */}
      <StaffNotificationsPanel />

      {/* Rehber */}
      <Card className="p-5 rounded-sm border-slate-800 bg-slate-950/40 space-y-2">
        <div className="text-lg font-semibold text-slate-100 flex items-center gap-2">
          <Info className="w-5 h-5 text-sky-400" /> Meta'dan Almanız Gereken Bilgiler
        </div>
        <ol className="list-decimal ml-5 space-y-1.5 text-sm text-slate-300">
          <li><b>Access Token</b> — Meta Business Settings → Users → <b>System Users</b> → yeni Sistem Kullanıcısı oluşturun; WABA'yı bu kullanıcıya atayın; <code className="text-slate-400 text-xs">business_management, whatsapp_business_messaging, whatsapp_business_management</code> izinleriyle uzun ömürlü token üretin.</li>
          <li><b>Phone Number ID</b> — Meta App → WhatsApp → <b>API Setup</b> sayfasında görünür. Test telefonunuza ait ID.</li>
          <li><b>WABA ID</b> — Aynı sayfada, <b>WhatsApp Business Account ID</b> olarak görünür.</li>
          <li><b>Verify Token</b> — Siz belirlersiniz, uzun rastgele bir string (örn. 32 karakter). Meta Console'daki Webhook Configuration'a aynısını girin.</li>
          <li><b>App Secret</b> — <i>(opsiyonel ama önerilen)</i> Meta App Dashboard → Settings → Basic. Webhook imza doğrulaması için.</li>
          <li><b>Template</b> — WhatsApp Manager → Message Templates'ta Türkçe onaylı template'ler oluşturun. Örn: <code className="text-slate-400 text-xs">siparis_olusturuldu_tr</code>, <code className="text-slate-400 text-xs">kargoya_verildi_tr</code>. Onaylanmadan gönderilemez.</li>
        </ol>
      </Card>
    </div>
  );
}

// ============================================================================
// Webhook Diagnostic Panel — /api/whatsapp/webhook/diagnostic verilerini gösterir
// ============================================================================

function outcomeBadge(o) {
  const map = {
    processed:              { cls: "bg-emerald-950 text-emerald-300 border-emerald-800", label: "İşlendi" },
    no_messages_or_statuses:{ cls: "bg-slate-900 text-slate-400 border-slate-700",       label: "Mesaj yok" },
    signature_mismatch:     { cls: "bg-rose-950 text-rose-300 border-rose-800",         label: "İmza hatası" },
    invalid_json:           { cls: "bg-rose-950 text-rose-300 border-rose-800",         label: "Bozuk JSON" },
    no_entries:             { cls: "bg-amber-950 text-amber-300 border-amber-800",     label: "Boş entry" },
    exception:              { cls: "bg-rose-950 text-rose-300 border-rose-800",         label: "Exception" },
    received:               { cls: "bg-slate-900 text-slate-400 border-slate-700",     label: "Alındı" },
  };
  const key = (o || "").split(":")[0];
  const style = map[key] || { cls: "bg-slate-900 text-slate-400 border-slate-700", label: o || "—" };
  return <span className={`px-2 py-0.5 rounded-sm text-[10px] uppercase tracking-widest font-mono border ${style.cls}`}>{style.label}</span>;
}

function extractMessagePreview(item) {
  // body_parsed içinden ilk mesaj bilgisini çıkar
  try {
    const bp = item.body_parsed;
    if (!bp || !bp.entry) return null;
    for (const e of bp.entry) {
      for (const c of e.changes || []) {
        const v = c.value || {};
        const phoneId = v.metadata?.phone_number_id;
        for (const m of v.messages || []) {
          const body = m.text?.body || m.button?.text || m.interactive?.button_reply?.title || "";
          return { phone_id: phoneId, from: m.from, type: m.type, text: body, id: m.id };
        }
      }
    }
    return null;
  } catch { return null; }
}

function WebhookDiagnosticPanel() {
  const [data, setData] = useState(null);
  const [rawItems, setRawItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [clearing, setClearing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [diag, raw] = await Promise.all([
        api.get("/whatsapp/webhook/diagnostic"),
        api.get("/whatsapp/webhook/raw", { params: { limit: 20 } }),
      ]);
      setData(diag.data);
      setRawItems(raw.data?.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const clearAll = async () => {
    if (!window.confirm("Tüm ham webhook logları silinsin mi? (Debug bittiğinde önerilir)")) return;
    setClearing(true);
    try {
      const { data } = await api.delete("/whatsapp/webhook/raw");
      toast.success(`${data.deleted} log silindi`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setClearing(false); }
  };

  const last = data?.items?.[0];
  // Son mesaj = son 20 içinde messages barındıran ilk kayıt
  const lastWithMsg = (data?.items || []).find((it) => (it.processed_messages || 0) > 0) || null;
  const rawLastWithMsg = rawItems.find((it) => (it.processed_messages || 0) > 0) || null;
  const lastMsg = rawLastWithMsg ? extractMessagePreview(rawLastWithMsg) : (lastWithMsg ? extractMessagePreview(lastWithMsg) : null);
  const metaReached = !!data?.meta_reachable || (data?.total_last_20 > 0);
  const outcomes = data?.outcome_counts || {};

  return (
    <Card className="p-5 rounded-sm border-slate-800 bg-slate-950/40 space-y-4" data-testid="wa-webhook-diagnostic">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4 text-blue-400" />
          <span className="text-sm font-semibold text-slate-200">Webhook Tanı / Son Kayıtlar</span>
          {data && <span className="text-xs font-mono text-slate-500">({data.total_last_20 || 0}/20)</span>}
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={load} disabled={loading} className="border-slate-700 rounded-sm" data-testid="wa-webhook-refresh">
            {loading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RefreshCcw className="w-3.5 h-3.5 mr-1" />}
            Webhook Testini Yenile
          </Button>
          <Button size="sm" variant="ghost" onClick={clearAll} disabled={clearing} className="text-slate-500 hover:text-rose-400" data-testid="wa-webhook-clear">
            <Trash2 className="w-3.5 h-3.5 mr-1" /> Logları Temizle
          </Button>
        </div>
      </div>

      {/* Config Status + Hints — SEC-004 ve Meta subscription tanısı */}
      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5 p-3 rounded-sm border border-slate-800 bg-slate-950/40"
               data-testid="wa-webhook-config-status">
            {[
              ["Verify Token",     data.config_status?.verify_token_set],
              ["Access Token",     data.config_status?.access_token_set],
              ["Phone Number ID",  data.config_status?.phone_number_id_set],
              ["WABA ID",          data.config_status?.waba_id_set],
              ["App Secret (SEC-004)", data.config_status?.app_secret_set],
              [`Dry Run: ${data.config_status?.dry_run ? "ON (gönderim durdurulmuş)" : "OFF (gerçek gönderim)"}`,
                !data.config_status?.dry_run],
            ].map(([label, ok]) => (
              <div key={label} className={`text-xs flex items-center gap-1.5 px-2 py-1 rounded-sm ${ok ? "text-emerald-300" : "text-amber-300"}`}>
                <span className={`inline-block w-2 h-2 rounded-full ${ok ? "bg-emerald-500" : "bg-amber-500"}`} />
                <span className="truncate">{label}</span>
              </div>
            ))}
          </div>
          {(data.hints || []).length > 0 && (
            <div className="rounded-sm border border-amber-800/60 bg-amber-950/20 p-3 space-y-1.5"
                 data-testid="wa-webhook-hints">
              <div className="text-xs uppercase font-mono tracking-widest text-amber-300 flex items-center gap-1">
                <AlertCircle className="w-3.5 h-3.5" /> Tanı Önerileri
              </div>
              <ul className="text-xs text-amber-100/90 space-y-1 list-disc list-inside">
                {(data.hints || []).map((h, i) => <li key={i}>{h}</li>)}
              </ul>
            </div>
          )}
        </>
      )}

      {/* KPI kartları */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <div className={`rounded-sm border p-3 ${metaReached ? "bg-emerald-950/30 border-emerald-800" : "bg-rose-950/30 border-rose-800"}`}>
          <div className="text-[10px] uppercase tracking-widest font-mono text-slate-400">Meta Ulaştı Mı?</div>
          <div className={`text-lg font-semibold mt-0.5 ${metaReached ? "text-emerald-300" : "text-rose-300"}`}>
            {metaReached ? "EVET ✓" : "HAYIR ✗"}
          </div>
        </div>
        <div className="rounded-sm border p-3 bg-slate-900/40 border-slate-800">
          <div className="text-[10px] uppercase tracking-widest font-mono text-slate-400">Son İstek</div>
          <div className="text-xs text-slate-100 mt-0.5 font-mono">
            {last?.received_at ? new Date(last.received_at).toLocaleString("tr-TR") : "—"}
          </div>
        </div>
        <div className="rounded-sm border p-3 bg-slate-900/40 border-slate-800">
          <div className="text-[10px] uppercase tracking-widest font-mono text-slate-400">İmza Uyuşmazlığı</div>
          <div className={`text-lg mt-0.5 ${data?.signature_mismatches ? "text-rose-300" : "text-slate-100"}`}>
            {data?.signature_mismatches ?? 0}
          </div>
        </div>
        <div className="rounded-sm border p-3 bg-slate-900/40 border-slate-800">
          <div className="text-[10px] uppercase tracking-widest font-mono text-slate-400">İşlenen Mesaj</div>
          <div className="text-lg mt-0.5 text-emerald-300">
            {(data?.items || []).reduce((s, it) => s + (it.processed_messages || 0), 0)}
          </div>
        </div>
      </div>

      {/* Outcome özeti */}
      {Object.keys(outcomes).length > 0 && (
        <div className="flex flex-wrap gap-2 text-xs">
          {Object.entries(outcomes).map(([k, v]) => (
            <div key={k} className="flex items-center gap-1.5">
              {outcomeBadge(k)}
              <span className="text-slate-400 font-mono">×{v}</span>
            </div>
          ))}
        </div>
      )}

      {/* Son mesaj hızlı görünüm */}
      {lastMsg && (
        <div className="rounded-sm border border-emerald-800 bg-emerald-950/20 p-3 text-xs space-y-1" data-testid="wa-webhook-last-msg">
          <div className="text-emerald-300 uppercase tracking-widest text-[10px] font-mono">Son Gelen Mesaj</div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-slate-300 font-mono">
            <div><span className="text-slate-500">Phone ID:</span> {lastMsg.phone_id || "—"}</div>
            <div><span className="text-slate-500">From:</span> {lastMsg.from || "—"}</div>
            <div><span className="text-slate-500">Tip:</span> {lastMsg.type || "—"}</div>
            <div><span className="text-slate-500">Msg ID:</span> {lastMsg.id?.slice(0, 24) || "—"}...</div>
          </div>
          {lastMsg.text && <div className="text-slate-100 mt-1 whitespace-pre-wrap break-words">"{lastMsg.text}"</div>}
        </div>
      )}

      {/* Son 20 webhook kaydı */}
      <div className="border border-slate-800 rounded-sm overflow-hidden">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-slate-950/70 text-slate-400 uppercase tracking-wider text-[10px]">
              <th className="text-left px-3 py-2">Zaman</th>
              <th className="text-left px-3 py-2">Outcome</th>
              <th className="text-left px-3 py-2">İmza</th>
              <th className="text-right px-3 py-2">Boyut</th>
              <th className="text-right px-3 py-2">Msg</th>
              <th className="text-right px-3 py-2">Status</th>
              <th className="text-left px-3 py-2">User-Agent</th>
              <th className="text-right px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {loading ? (
              <tr><td colSpan={8} className="text-center py-6 text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Yükleniyor...</td></tr>
            ) : rawItems.length === 0 ? (
              <tr><td colSpan={8} className="text-center py-6 text-slate-500">
                Henüz webhook kaydı yok.<br/>
                <span className="text-[11px]">Telefonunuzdan işletme numarasına mesaj atıp "Yenile"ye basın.</span>
              </td></tr>
            ) : rawItems.map((it, idx) => {
              const isExp = expandedId === idx;
              const preview = extractMessagePreview(it);
              return (
                <Fragment key={idx}>
                  <tr className="text-slate-300 hover:bg-slate-900/40 cursor-pointer"
                    onClick={() => setExpandedId(isExp ? null : idx)}
                    data-testid={`wa-webhook-row-${idx}`}>
                    <td className="px-3 py-2 font-mono text-[11px] text-slate-400">
                      {it.received_at ? new Date(it.received_at).toLocaleTimeString("tr-TR") : "—"}
                    </td>
                    <td className="px-3 py-2">{outcomeBadge(it.outcome)}</td>
                    <td className="px-3 py-2 font-mono text-[10px]">
                      {it.signature_ok === true ? <span className="text-emerald-400">✓ ok</span>
                       : it.signature_ok === false ? <span className="text-rose-400">✗ hata</span>
                       : <span className="text-slate-500">yok</span>}
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-[11px]">{it.size ?? 0}b</td>
                    <td className="px-3 py-2 text-right font-mono">{it.processed_messages ?? 0}</td>
                    <td className="px-3 py-2 text-right font-mono">{it.processed_statuses ?? 0}</td>
                    <td className="px-3 py-2 text-slate-500 text-[10px] font-mono truncate max-w-[180px]" title={(it.headers || {})["user-agent"]}>
                      {(it.headers || {})["user-agent"]?.slice(0, 32) || "—"}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {isExp ? <ChevronDown className="w-3.5 h-3.5 inline text-slate-500" /> : <ChevronRight className="w-3.5 h-3.5 inline text-slate-500" />}
                    </td>
                  </tr>
                  {isExp && (
                    <tr>
                      <td colSpan={8} className="bg-slate-950/70 px-3 py-3">
                        <div className="space-y-2 text-[11px]">
                          {preview && (
                            <div className="grid grid-cols-2 gap-x-4 font-mono text-slate-300">
                              <div><span className="text-slate-500">phone_number_id:</span> {preview.phone_id || "—"}</div>
                              <div><span className="text-slate-500">from:</span> {preview.from || "—"}</div>
                              <div><span className="text-slate-500">type:</span> {preview.type || "—"}</div>
                              <div><span className="text-slate-500">text.body:</span> {preview.text || "—"}</div>
                            </div>
                          )}
                          {it.error && (
                            <div className="text-rose-400">
                              <div className="font-semibold">Hata:</div>
                              <pre className="whitespace-pre-wrap font-mono text-[10px]">{it.error}</pre>
                            </div>
                          )}
                          <details>
                            <summary className="text-slate-400 cursor-pointer">Ham JSON</summary>
                            <pre className="whitespace-pre-wrap font-mono text-[10px] text-slate-500 bg-slate-950 p-2 rounded-sm border border-slate-800 mt-1 max-h-64 overflow-auto">
                              {(it.body_text || "").slice(0, 4000)}
                            </pre>
                          </details>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="text-[11px] text-slate-500 border-t border-slate-800 pt-3">
        <b className="text-slate-400">İpucu:</b> Meta Ulaştı Mı = <b>HAYIR</b> ise webhook URL'nin Meta App Dashboard'da yanlış olabileceğini kontrol edin.
        İmza uyuşmazlığı varsa App Secret'ı yeniden kopyalayın. no_messages_or_statuses görüyorsanız Meta Dashboard'da
        Webhook Fields'da <code className="text-slate-400">messages</code> subscribe edilmiş olmalı.
      </div>
    </Card>
  );
}

// ============================================================================
// Otomatik Bildirimler — Sipariş / Sevkiyat olaylarında müşteriye WhatsApp
// ============================================================================
function AutomationPanel() {
  const [cfg, setCfg] = useState({ events: {}, catalog: [], variables_help: {} });
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [logsLoading, setLogsLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/whatsapp/automation");
      setCfg({
        events: data.events || {},
        catalog: data.catalog || [],
        variables_help: data.variables_help || {},
      });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  const loadLogs = async () => {
    setLogsLoading(true);
    try {
      const { data } = await api.get("/whatsapp/automation/logs?limit=50");
      setLogs(data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLogsLoading(false); }
  };
  useEffect(() => { load(); loadLogs(); }, []);

  const patch = (key, patch) => {
    setCfg((prev) => ({
      ...prev,
      events: { ...prev.events, [key]: { ...(prev.events[key] || {}), ...patch } },
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const events = {};
      cfg.catalog.forEach((c) => {
        const e = cfg.events[c.key] || {};
        events[c.key] = { enabled: !!e.enabled, text: e.text || "" };
      });
      const { data } = await api.patch("/whatsapp/automation", { events });
      setCfg({ events: data.events, catalog: data.catalog || cfg.catalog,
               variables_help: data.variables_help || cfg.variables_help });
      toast.success("Otomasyon ayarları kaydedildi");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const statusBadge = (s) => {
    const map = {
      sent:    "bg-emerald-950 text-emerald-300 border-emerald-800",
      failed:  "bg-rose-950 text-rose-300 border-rose-800",
      skipped: "bg-slate-900 text-slate-400 border-slate-700",
    };
    return <span className={`inline-block text-[10px] uppercase font-mono px-1.5 py-0.5 rounded-sm border ${map[s] || map.skipped}`}>{s}</span>;
  };

  return (
    <Card
      className="p-5 rounded-sm border-slate-800 bg-slate-950/40 space-y-4"
      data-testid="whatsapp-automation-panel"
    >
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-lg font-semibold text-slate-100 flex items-center gap-2">
            <Send className="w-5 h-5 text-blue-400" /> Otomatik Bildirimler
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            Sipariş ve sevkiyat olaylarında müşterinin WhatsApp'ına bildirim gönderilir.
            Kısa mesajlaşma için Meta'nın 24 saat penceresi geçerlidir; pencere dışındaki
            mesajlar hata olarak loglanır.
          </div>
        </div>
        <Button
          data-testid="wa-automation-save"
          onClick={save} disabled={saving || loading}
          className="rounded-sm bg-blue-600 hover:bg-blue-500 shrink-0"
        >
          {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
          Kaydet
        </Button>
      </div>

      {loading ? (
        <div className="text-slate-500 text-sm py-3"><Loader2 className="inline w-4 h-4 mr-2 animate-spin" /> Yükleniyor…</div>
      ) : (
        <div className="space-y-3">
          {cfg.catalog.map((ev) => {
            const e = cfg.events[ev.key] || {};
            const vars = (cfg.variables_help || {})[ev.key] || [];
            return (
              <div
                key={ev.key}
                data-testid={`wa-automation-event-${ev.key}`}
                className={`border rounded-sm p-3 ${e.enabled ? "border-blue-800/60 bg-blue-950/10" : "border-slate-800 bg-slate-950/30"}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-100">{ev.label}</div>
                    <div className="text-[10px] font-mono uppercase text-slate-500">{ev.key}</div>
                  </div>
                  <label className="flex items-center gap-2 shrink-0 cursor-pointer">
                    <span className="text-[10px] font-mono uppercase text-slate-400">
                      {e.enabled ? "Açık" : "Kapalı"}
                    </span>
                    <Switch
                      checked={!!e.enabled}
                      onCheckedChange={(v) => patch(ev.key, { enabled: v })}
                      data-testid={`wa-automation-toggle-${ev.key}`}
                    />
                  </label>
                </div>
                <div className="mt-2">
                  <textarea
                    data-testid={`wa-automation-text-${ev.key}`}
                    rows={4}
                    value={e.text || ""}
                    onChange={(ev2) => patch(ev.key, { text: ev2.target.value })}
                    placeholder="Mesaj metni…"
                    className="w-full bg-slate-950 border border-slate-800 rounded-sm px-3 py-2 text-sm text-slate-100 placeholder:text-slate-600 font-mono focus:border-blue-500 focus:outline-none"
                  />
                  {vars.length > 0 && (
                    <div className="mt-1.5 flex flex-wrap gap-1">
                      <span className="text-[10px] uppercase font-mono text-slate-500 mr-1">Değişkenler:</span>
                      {vars.map((v) => (
                        <code
                          key={v}
                          onClick={() => {
                            const insert = `{${v}}`;
                            patch(ev.key, { text: (e.text || "") + insert });
                          }}
                          className="text-[10px] px-1.5 py-0.5 bg-slate-900 hover:bg-slate-800 border border-slate-700 rounded-sm cursor-pointer text-blue-300 transition-colors"
                          title="Metnin sonuna eklemek için tıklayın"
                        >
                          {`{${v}}`}
                        </code>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Otomasyon Logları */}
      <div className="pt-4 border-t border-slate-800">
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm font-semibold text-slate-100 flex items-center gap-2">
            <ListChecks className="w-4 h-4 text-sky-400" /> Otomasyon Kayıtları
            <span className="text-xs font-mono text-slate-500">({logs.length})</span>
          </div>
          <Button onClick={loadLogs} disabled={logsLoading} size="sm" variant="ghost" className="rounded-sm"
                  data-testid="wa-automation-logs-refresh">
            {logsLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCcw className="w-4 h-4" />}
          </Button>
        </div>
        {logs.length === 0 ? (
          <div className="text-xs text-slate-500 py-4 text-center border border-dashed border-slate-800 rounded-sm">
            Henüz kayıt yok. Sipariş/sevkiyat olayları geldikçe burada listelenir.
          </div>
        ) : (
          <div className="border border-slate-800 rounded-sm overflow-hidden">
            <table className="w-full text-xs" data-testid="wa-automation-logs-table">
              <thead className="bg-slate-900/60 text-slate-400">
                <tr className="text-left">
                  <th className="px-2 py-1.5 font-mono uppercase text-[9px]">Tarih</th>
                  <th className="px-2 py-1.5 font-mono uppercase text-[9px]">Olay</th>
                  <th className="px-2 py-1.5 font-mono uppercase text-[9px]">Müşteri</th>
                  <th className="px-2 py-1.5 font-mono uppercase text-[9px]">Telefon</th>
                  <th className="px-2 py-1.5 font-mono uppercase text-[9px]">Durum</th>
                  <th className="px-2 py-1.5 font-mono uppercase text-[9px]">Neden / Detay</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/60">
                {logs.map((l) => (
                  <tr key={l.id} data-testid={`wa-automation-log-${l.id}`}>
                    <td className="px-2 py-1.5 font-mono text-slate-400">{new Date(l.created_at).toLocaleString("tr-TR")}</td>
                    <td className="px-2 py-1.5 text-slate-300">{l.event_key}</td>
                    <td className="px-2 py-1.5 text-slate-200">{l.customer_name || "—"}</td>
                    <td className="px-2 py-1.5 font-mono text-slate-400">{l.to_phone || "—"}</td>
                    <td className="px-2 py-1.5">{statusBadge(l.status)}</td>
                    <td className="px-2 py-1.5 text-slate-400 max-w-[260px] truncate" title={l.error_detail || l.message_preview || ""}>
                      <span className="font-mono text-[10px] text-slate-500">{l.reason}</span>
                      {l.error_detail && <span className="ml-1 text-rose-400">· {l.error_detail}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Card>
  );
}

// ============================================================================
// Personel WhatsApp Bildirimleri — Ayarlar > WhatsApp içine gömülü panel
// ============================================================================
function StaffNotificationsPanel() {
  const [cfg, setCfg] = useState({ enabled: false, events: {}, catalog: [] });
  const [logs, setLogs] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testUserId, setTestUserId] = useState("");
  const [testEvent, setTestEvent] = useState("task_assigned");
  const [testBusy, setTestBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: c }, { data: l }, { data: u }] = await Promise.all([
        api.get("/staff-notifications/config"),
        api.get("/staff-notifications/logs?limit=50"),
        api.get("/users/directory"),
      ]);
      setCfg({ enabled: !!c.enabled, events: c.events || {}, catalog: c.catalog || [] });
      setLogs(l.items || []);
      setUsers(u || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const patch = (key, part) => {
    setCfg((prev) => ({
      ...prev,
      events: { ...prev.events, [key]: { ...(prev.events[key] || {}), ...part } },
    }));
  };
  const save = async () => {
    setSaving(true);
    try {
      const events = {};
      cfg.catalog.forEach((c) => {
        const e = cfg.events[c.key] || {};
        events[c.key] = {
          enabled: !!e.enabled,
          text: e.text || "",
          template_name: e.template_name || "",
          template_lang: e.template_lang || "tr",
        };
      });
      const { data } = await api.patch("/staff-notifications/config",
                                        { enabled: !!cfg.enabled, events });
      setCfg({ enabled: !!data.enabled, events: data.events || {}, catalog: data.catalog || cfg.catalog });
      toast.success("Personel bildirim ayarları kaydedildi");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  const sendTest = async () => {
    if (!testUserId) { toast.error("Test alıcısı seçin"); return; }
    setTestBusy(true);
    try {
      const { data } = await api.post("/staff-notifications/test", {
        kind: "user", recipient_id: testUserId, event_key: testEvent,
      });
      toast[data.status === "sent" ? "success" : data.status === "skipped" ? "info" : "error"](
        `Test: ${data.status} · ${data.reason}${data.error_detail ? " · " + data.error_detail : ""}`,
        { duration: 6000 }
      );
      const { data: l } = await api.get("/staff-notifications/logs?limit=50");
      setLogs(l.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setTestBusy(false); }
  };
  const enableAll = (val) => {
    setCfg((prev) => {
      const nx = { ...prev.events };
      (prev.catalog || []).forEach((c) => { nx[c.key] = { ...(nx[c.key] || {}), enabled: val }; });
      return { ...prev, events: nx };
    });
  };
  const statusBadge = (s) => {
    const map = {
      sent:    "bg-emerald-950 text-emerald-300 border-emerald-800",
      failed:  "bg-rose-950 text-rose-300 border-rose-800",
      skipped: "bg-slate-900 text-slate-400 border-slate-700",
    };
    return <span className={`inline-block text-[10px] uppercase font-mono px-1.5 py-0.5 rounded-sm border ${map[s] || map.skipped}`}>{s}</span>;
  };
  return (
    <Card className="p-5 rounded-sm border-slate-800 bg-slate-950/40 space-y-4"
          data-testid="staff-notifications-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-lg font-semibold text-slate-100 flex items-center gap-2">
            <Users className="w-5 h-5 text-blue-400" /> Personel Bildirimleri
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            ERP kullanıcılarına ve çalışanlara görev, sevkiyat, PDKS vs. olaylarında WhatsApp bildirimi.
            24 saat penceresi içinde metin, dışında APPROVED template kullanılır.
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <label className="flex items-center gap-2 text-xs uppercase font-mono text-slate-400 cursor-pointer">
            <Switch checked={cfg.enabled}
                    onCheckedChange={(v) => setCfg({ ...cfg, enabled: v })}
                    data-testid="staff-notif-master-toggle" />
            Ana Anahtar {cfg.enabled ? "AÇIK" : "KAPALI"}
          </label>
          <Button data-testid="staff-notif-save" onClick={save} disabled={saving || loading}
                  className="rounded-sm bg-blue-600 hover:bg-blue-500">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
            Kaydet
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-slate-500 text-sm py-3"><Loader2 className="inline w-4 h-4 mr-2 animate-spin" /> Yükleniyor…</div>
      ) : (
        <>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => enableAll(true)}
                    className="border-slate-700 rounded-sm text-xs" data-testid="staff-notif-enable-all">
              Tümünü Aç
            </Button>
            <Button size="sm" variant="outline" onClick={() => enableAll(false)}
                    className="border-slate-700 rounded-sm text-xs" data-testid="staff-notif-disable-all">
              Tümünü Kapat
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {(cfg.catalog || []).map((c) => {
              const e = cfg.events[c.key] || {};
              return (
                <div key={c.key}
                     className={`border rounded-sm p-2.5 ${e.enabled ? "border-blue-800/60 bg-blue-950/10" : "border-slate-800 bg-slate-950/30"}`}
                     data-testid={`staff-notif-event-${c.key}`}>
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-slate-100 truncate">{c.label}</div>
                      <div className="text-[10px] font-mono uppercase text-slate-500">{c.key}</div>
                    </div>
                    <Switch checked={!!e.enabled}
                            onCheckedChange={(v) => patch(c.key, { enabled: v })}
                            data-testid={`staff-notif-toggle-${c.key}`} />
                  </div>
                  <textarea rows={2} value={e.text || ""}
                            onChange={(ev) => patch(c.key, { text: ev.target.value })}
                            placeholder="Metin şablonu (24h içinde)…"
                            className="w-full bg-slate-950 border border-slate-800 rounded-sm px-2 py-1 text-xs text-slate-100 font-mono focus:border-blue-500 focus:outline-none"
                            data-testid={`staff-notif-text-${c.key}`} />
                  <div className="flex gap-1 mt-1">
                    <input value={e.template_name || ""}
                           onChange={(ev) => patch(c.key, { template_name: ev.target.value })}
                           placeholder="APPROVED template adı (24h dışı için)"
                           className="flex-1 bg-slate-950 border border-slate-800 rounded-sm px-2 py-1 text-[11px] text-slate-200 font-mono focus:border-blue-500 focus:outline-none"
                           data-testid={`staff-notif-tpl-${c.key}`} />
                    <input value={e.template_lang || "tr"}
                           onChange={(ev) => patch(c.key, { template_lang: ev.target.value })}
                           placeholder="lang"
                           className="w-14 bg-slate-950 border border-slate-800 rounded-sm px-1 py-1 text-[11px] text-slate-300 font-mono focus:border-blue-500 focus:outline-none" />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Test send */}
          <div className="pt-3 border-t border-slate-800 space-y-2">
            <div className="text-sm font-semibold text-slate-100">Test Gönder</div>
            <div className="flex flex-wrap gap-2 items-end">
              <select value={testUserId} onChange={(e) => setTestUserId(e.target.value)}
                      data-testid="staff-notif-test-user"
                      className="bg-slate-950 border border-slate-800 rounded-sm px-2 py-1.5 text-xs text-slate-100">
                <option value="">— Kullanıcı seçin —</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>{u.name || u.email} ({u.email})</option>
                ))}
              </select>
              <select value={testEvent} onChange={(e) => setTestEvent(e.target.value)}
                      data-testid="staff-notif-test-event"
                      className="bg-slate-950 border border-slate-800 rounded-sm px-2 py-1.5 text-xs text-slate-100">
                {(cfg.catalog || []).map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
              <Button onClick={sendTest} disabled={testBusy || !testUserId} size="sm"
                      className="rounded-sm bg-blue-600 hover:bg-blue-500"
                      data-testid="staff-notif-test-btn">
                {testBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />}
                Test Yolla
              </Button>
            </div>
          </div>

          {/* Logs */}
          <div className="pt-3 border-t border-slate-800">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-semibold text-slate-100 flex items-center gap-2">
                <ListChecks className="w-4 h-4 text-sky-400" /> Gönderim Kayıtları
                <span className="text-xs font-mono text-slate-500">({logs.length})</span>
              </div>
              <Button onClick={load} size="sm" variant="ghost" className="rounded-sm"
                      data-testid="staff-notif-logs-refresh">
                <RefreshCcw className="w-4 h-4" />
              </Button>
            </div>
            {logs.length === 0 ? (
              <div className="text-xs text-slate-500 py-4 text-center border border-dashed border-slate-800 rounded-sm">
                Henüz kayıt yok.
              </div>
            ) : (
              <div className="border border-slate-800 rounded-sm overflow-hidden">
                <table className="w-full text-xs" data-testid="staff-notif-logs-table">
                  <thead className="bg-slate-900/60 text-slate-400">
                    <tr className="text-left">
                      <th className="px-2 py-1.5 font-mono uppercase text-[9px]">Tarih</th>
                      <th className="px-2 py-1.5 font-mono uppercase text-[9px]">Olay</th>
                      <th className="px-2 py-1.5 font-mono uppercase text-[9px]">Alıcı</th>
                      <th className="px-2 py-1.5 font-mono uppercase text-[9px]">Telefon</th>
                      <th className="px-2 py-1.5 font-mono uppercase text-[9px]">Tür</th>
                      <th className="px-2 py-1.5 font-mono uppercase text-[9px]">Durum</th>
                      <th className="px-2 py-1.5 font-mono uppercase text-[9px]">Neden / Hata</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800/60">
                    {logs.map((l) => (
                      <tr key={l.id}>
                        <td className="px-2 py-1.5 font-mono text-slate-400">{new Date(l.created_at).toLocaleString("tr-TR")}</td>
                        <td className="px-2 py-1.5 text-slate-300">{l.event_key}</td>
                        <td className="px-2 py-1.5 text-slate-200">{l.recipient_name || l.recipient_email || "—"}</td>
                        <td className="px-2 py-1.5 font-mono text-slate-400">{l.phone || "—"}</td>
                        <td className="px-2 py-1.5 text-slate-400 uppercase text-[10px]">{l.msg_type}</td>
                        <td className="px-2 py-1.5">{statusBadge(l.status)}</td>
                        <td className="px-2 py-1.5 text-slate-400 max-w-[260px] truncate" title={l.error_detail || l.message_preview || ""}>
                          <span className="font-mono text-[10px] text-slate-500">{l.reason}</span>
                          {l.error_detail && <span className="ml-1 text-rose-400">· {l.error_detail}</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </Card>
  );
}
